import type {
  ITeacherClass,
  ITeacherDetails,
  ITeacherStudent,
  ITeacherStudentsQuery,
} from '@/types/teacher';

import { enqueueSnackbar } from 'notistack';
import { Activity, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';

import { IconTrash, IconSearch } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import ClassSummaryCard from '@/components/ClassSummaryCard';
import ConfirmationModal from '@/components/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import TeacherDetailsSkeleton from '@/components/SkeletonLoader/TeacherDetailsSkeleton';
import { ERestoreEntityType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { StudentDetailDrawer } from '@/pages/school/classDetails/components/StudentDetailDrawer';
import {
  removeTeacher,
  getTeacherClasses,
  getTeacherStudents,
} from '@/service/teacherService';
import { useSkeletonStore } from '@/store/useSkeletonStore';

import StudentProgressTable from './components/StudentProgressTable';
import TeacherProfile from './components/TeacherProfile';

const TeacherDetails = () => {
  const { formatText } = useFormatText();
  const { setIsBreadcrumb } = usePageTitle();
  const navigate = useNavigate();
  const { id: teacherId } = useParams<{ id: string }>();
  const [teacher, setTeacher] = useState<ITeacherDetails | null>(null);
  const [classes, setClasses] = useState<ITeacherClass[]>([]);
  const [students, setStudents] = useState<ITeacherStudent[]>([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [studentSortField, setStudentSortField] = useState('');
  const [studentSortDir, setStudentSortDir] = useState('');
  const { isScreenLoading, setScreenLoading, isTableLoading, setTableLoading } =
    useSkeletonStore();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [classFilter, setClassFilter] = useState('');
  const [selectedStudent, setSelectedStudent] =
    useState<ITeacherStudent | null>(null);
  const [isStudentDrawerOpen, setIsStudentDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchDetails = useCallback(async () => {
    if (!teacherId) return;
    setScreenLoading(true);
    try {
      const res = await getTeacherClasses(teacherId);
      if (res?.data) {
        setTeacher(res.data);
        setClasses(res.data.classes || []);
      }
    } finally {
      setScreenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (teacher) {
      const breadcrumb = [
        {
          label: formatText('breadcrumb.teachers'),
          to: '/school/teachers-classes?tab=teachers',
        },
        {
          label: teacher.teacher_name,
        },
      ];
      setIsBreadcrumb(breadcrumb);
    }
    return () => {
      setIsBreadcrumb([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacher]);

  const { register, watch } = useForm({
    defaultValues: {
      studentSearch: '',
    },
  });

  const studentSearch = watch('studentSearch');
  const debouncedSearch = useDebounce(studentSearch, 500);

  const fetchStudents = useCallback(async () => {
    if (!teacherId) return;

    // Only set table loading if we are fetching new data
    // We do NOT want to set this if we are just re-rendering or if it's a background update if we had one
    // But here it is engaged by search/filter so yes we want it.
    setTableLoading(true);

    const query: ITeacherStudentsQuery = {
      limit: 10,
      offset: (currentPage - 1) * 10,
    };

    if (debouncedSearch) query.search = debouncedSearch;
    if (classFilter) query.class_id = classFilter;
    if (studentSortField && studentSortDir) {
      query[`order[${studentSortField}]`] = studentSortDir;
    }

    try {
      const res = await getTeacherStudents(teacherId, query);
      if (res?.status && Array.isArray(res.data)) {
        setStudents(res.data as ITeacherStudent[]);
        setStudentsTotal(res.total ?? res.data.length);
      } else {
        setStudents([]);
        setStudentsTotal(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Delay closing slightly for smooth transition or just close immediately
      setTableLoading(false);
    }
  }, [
    teacherId,
    currentPage,
    debouncedSearch,
    classFilter,
    studentSortField,
    studentSortDir,
    setTableLoading, // Add dependency
  ]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, classFilter, studentSortField, studentSortDir]);

  const classFilterOptions = classes.map((c) => ({
    id: c.id,
    name: c.name,
  }));

  const handleStudentSort = (key: string) => {
    if (studentSortField === key) {
      if (studentSortDir === 'ASC') setStudentSortDir('DESC');
      else if (studentSortDir === 'DESC') {
        setStudentSortField('');
        setStudentSortDir('');
      } else setStudentSortDir('ASC');
    } else {
      setStudentSortField(key);
      setStudentSortDir('ASC');
    }
  };

  const handleRemoveTeacher = async () => {
    if (!teacherId) return;
    try {
      const response = await removeTeacher(
        teacherId,
        ERestoreEntityType.TEACHER
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setIsRemoveModalOpen(false);
        navigate('/school/teachers-classes?tab=teachers');
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (isScreenLoading || !teacher) {
    return <TeacherDetailsSkeleton />;
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="teacher-details-page-container"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Input
            name="studentSearch"
            register={register}
            placeholder={formatText('teacher-details.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={18} className="text-neutral-500" />,
            }}
            className="w-72"
            inputClassName="h-10"
            dataTestId="student-search-input"
          />
          <Select
            options={classFilterOptions}
            selected={classFilter}
            onChange={(option: string | string[]) =>
              setClassFilter(Array.isArray(option) ? option[0] || '' : option)
            }
            placeholder={formatText('teacher-details.class-filter')}
            isClearable
            className="min-w-37.5"
            dataTestId="class-filter-select"
          />
        </div>

        <Button
          label={formatText('teacher-details.remove-teacher')}
          variant="outline"
          desructive
          icon={<IconTrash size={18} />}
          className="h-10 px-4 cursor-pointer"
          onClick={() => setIsRemoveModalOpen(true)}
          dataTestId="remove-teacher-button"
        />
      </div>

      <TeacherProfile teacher={teacher} />

      <div
        className="flex gap-6 flex-wrap"
        data-testid="assigned-classes-section"
      >
        <div className="flex w-full justify-between items-center gap-4">
          <p className="text-neutral-900 text-lg font-semibold leading-7">
            {formatText('teacher-details.assigned-classes')}
          </p>

          <Button
            label={formatText('common.actions.view-all')}
            variant="outline"
            onClick={() =>
              navigate(
                `/school/teachers-classes/teacher-details/${teacherId}/assigned-classes`
              )
            }
            dataTestId="view-all-classes-button"
          />
        </div>
        <div
          className="flex gap-6 flex-wrap w-full"
          data-testid="assigned-classes-list"
        >
          {classes.length === 0 && (
            <EmptyState
              title={formatText('common.no-data-found')}
              className="h-55!"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            {classes.map((cls, index) => (
              <div key={cls.id} data-testid={`class-card-${cls.id}`}>
                <ClassSummaryCard
                  name={cls?.name}
                  studentsCount={cls?.total_students}
                  lessonsCount={cls?.total_lessons}
                  colorIndex={index}
                  status={cls?.status}
                  onClick={() =>
                    navigate(
                      `/school/teachers-classes/teacher-details/${teacherId}/class/${cls?.id}`,
                      { state: { className: cls?.name } }
                    )
                  }
                  className="h-full max-w-full! cursor-pointer"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div data-testid="student-progress-table-section">
        <StudentProgressTable
          students={students}
          total={studentsTotal}
          sortField={studentSortField}
          sortDir={studentSortDir}
          onSort={handleStudentSort}
          isLoading={isTableLoading}
          onRowClick={(id) => {
            const student = students.find((s) => s.id === id);
            if (student) {
              setSelectedStudent(student);
              setIsStudentDrawerOpen(true);
            }
          }}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>

      <Activity mode={isRemoveModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveModalOpen}
          setIsOpen={setIsRemoveModalOpen}
          title="teachers-classes.remove-teacher.title"
          descriptionPrefix="teachers-classes.remove-teacher.descriptionPrefix"
          descriptionSuffix="teachers-classes.remove-teacher.descriptionSuffix"
          entityName={teacher.teacher_name}
          confirmLabel={formatText('teachers-classes.remove-teacher.confirm')}
          onConfirm={handleRemoveTeacher}
          isDestructive
        />
      </Activity>

      <Activity mode={isStudentDrawerOpen ? 'visible' : 'hidden'}>
        <StudentDetailDrawer
          isOpen={isStudentDrawerOpen}
          onClose={() => {
            setIsStudentDrawerOpen(false);
            setSelectedStudent(null);
          }}
          student={selectedStudent}
        />
      </Activity>
    </div>
  );
};
export default TeacherDetails;
